// constants/paymentScenarios.js
// Shared constants for payment scenarios using exact original text from components

export const PAYMENT_STATUS = {
  SUCCESS: 'success',
  CURRENT: 'current',
  OVERDUE: 'overdue',
  DISCONNECTED: 'disconnected'
};

export const PAYMENT_SCENARIOS = {
  SUCCESS: {
    status: PAYMENT_STATUS.SUCCESS,
    title: 'Payment Successful',
    description: 'Payment completed successfully - service restored',
    headerStatus: 'Payment Successful - Service Restored',
    headerClass: 'status-success',
    templatePrefix: 'success'
  },
  GARDENWALL: {
    status: PAYMENT_STATUS.CURRENT,
    title: 'GardenWall',
    description:
      'Template for reminding subscribers of an upcoming bill due date. It explains that payment should be made by the due date to avoid any service interruptions.',
    headerStatus: 'Account Current - All Services Active',
    headerClass: 'status-current',
    templatePrefix: 'current-payment'
  },
  DUE_DATE: {
    days: 0,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Payment Not Received on Due Date',
    description:
      'Template for informing subscribers who missed their bill due date. It explains that certain features may be discontinued until payment is made.',
    headerStatus: 'Payment Not Received on Due Date',
    headerClass: 'status-due',
    templatePrefix: 'delayed-payments-1'
  },
  OVERDUE_30: {
    days: 30,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 30 Days Past Due',
    description:
      'Template for reminding subscribers who have not paid their bill for over 30 days. This serves as an early reminder to avoid service interruptions.',
    headerStatus: 'Over 30 Days Past Due',
    headerClass: 'status-overdue',
    templatePrefix: 'delayed-payments-30'
  },
  OVERDUE_60: {
    days: 60,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 60 Days Past Due',
    description:
      'Template for notifying subscribers who have not paid their bill for over 60 days. It highlights the urgency of payment to prevent further restrictions.',
    headerStatus: 'Over 60 Days Past Due',
    headerClass: 'status-critical',
    templatePrefix: 'delayed-payments-60'
  },
  OVERDUE_90: {
    days: 90,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 90 Days Past Due',
    description:
      'Template for alerting subscribers who have not paid their bill for over 90 days. This acts as a final warning before permanent service termination.',
    headerStatus: 'Over 90 Days Past Due',
    headerClass: 'status-suspended',
    templatePrefix: 'delayed-payments-90'
  },
  OVERDUE_120: {
    days: 120,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 120 Days Past Due',
    description:
      'Template for alerting subscribers who have not paid their bill for over 120 days. This acts as a final warning before permanent service termination.',
    headerStatus: 'Over 120 Days Past Due',
    headerClass: 'status-critical',
    templatePrefix: 'delayed-payments-120'
  },
  OVERDUE_150: {
    days: 150,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 150 Days Past Due',
    description:
      'Template for alerting subscribers who have not paid their bill for over 150 days. This acts as a final warning before permanent service termination.',
    headerStatus: 'Over 150 Days Past Due',
    headerClass: 'status-critical',
    templatePrefix: 'delayed-payments-150'
  },
  OVERDUE_180: {
    days: 180,
    status: PAYMENT_STATUS.OVERDUE,
    title: 'Over 180 Days Past Due',
    description:
      'Template for alerting subscribers who have not paid their bill for over 180 days. This acts as a final warning before permanent service termination.',
    headerStatus: 'Over 180 Days Past Due',
    headerClass: 'status-suspended',
    templatePrefix: 'delayed-payments-180'
  },
  SERVICE_DISCONNECTED: {
    status: PAYMENT_STATUS.DISCONNECTED,
    title: 'Service Disconnected',
    description:
      'Template for notifying subscribers when their service has been temporarily disconnected due to overdue payment. Immediate action required to restore service.',
    headerStatus: 'Service Temporarily Disconnected',
    headerClass: 'status-disconnected',
    templatePrefix: 'disconnected'
  }
};

// ✅ Helper: get scenario by days
export const getScenarioByDays = (days) => {
  const scenarios = Object.values(PAYMENT_SCENARIOS);
  return scenarios.find((scenario) => scenario.days === days) || PAYMENT_SCENARIOS.OVERDUE_30;
};

// ✅ Helper: get scenario by status (overdue can use days if provided)
export const getScenarioByStatus = (status, days = null) => {
  if (status === PAYMENT_STATUS.OVERDUE && days !== null) {
    return getScenarioByDays(days);
  }
  const scenarios = Object.values(PAYMENT_SCENARIOS);
  return scenarios.find((scenario) => scenario.status === status) || PAYMENT_SCENARIOS.GARDENWALL;
};

// ✅ Helper: get scenario content for Dashboard
export const getScenarioContent = (paymentScenario) => {
  if (!paymentScenario) {
    return {
      headerStatus: PAYMENT_SCENARIOS.GARDENWALL.headerStatus,
      headerClass: PAYMENT_SCENARIOS.GARDENWALL.headerClass
    };
  }

  // check days first
  if (paymentScenario.days !== undefined && paymentScenario.days !== null) {
    const scenario = getScenarioByDays(paymentScenario.days);
    return {
      headerStatus: scenario.headerStatus,
      headerClass: scenario.headerClass
    };
  }

  // fallback to status
  if (paymentScenario.status) {
    const scenario = getScenarioByStatus(paymentScenario.status, paymentScenario.days);
    return {
      headerStatus: scenario.headerStatus,
      headerClass: scenario.headerClass
    };
  }

  return {
    headerStatus: PAYMENT_SCENARIOS.GARDENWALL.headerStatus,
    headerClass: PAYMENT_SCENARIOS.GARDENWALL.headerClass
  };
};

// ✅ Helper: get template prefix
export const getTemplatePrefix = (paymentScenario) => {
  if (!paymentScenario) return PAYMENT_SCENARIOS.GARDENWALL.templatePrefix;

  // check days first (avoid "overdue" always defaulting to day 0)
  if (paymentScenario.days !== undefined && paymentScenario.days !== null) {
    const scenario = getScenarioByDays(paymentScenario.days);
    return scenario.templatePrefix;
  }

  // fallback: status
  if (paymentScenario.status) {
    const scenario = getScenarioByStatus(paymentScenario.status, paymentScenario.days);
    return scenario.templatePrefix;
  }

  return PAYMENT_SCENARIOS.GARDENWALL.templatePrefix;
};

